import { FormattedMessage, useIntl } from 'react-intl';

import LoadingModal from 'src/components/navigation/LoadingModal';
import { useExportPaymentsNRA } from 'src/features/statistics/api/useExportPaymentsNRA';
import { statisticsMessages } from 'src/features/statistics/utils';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { GeneralSmartParkError } from 'src/types';
import dayjs from 'dayjs';

type Props = {
    parkingId: string;
    dateTime: {
        beforeDate: Date;
        afterDate: Date;
    };
    disabled: boolean;
};

const toDateParam = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const extractErrorMessage = async (error: GeneralSmartParkError): Promise<string | undefined> => {
    const data: unknown = error.response?.data;
    let payload: unknown = data;

    if (data instanceof Blob) {
        try {
            payload = JSON.parse(await data.text());
        } catch {
            return undefined;
        }
    }

    if (Array.isArray(payload)) return typeof payload[0] === 'string' ? payload[0] : undefined;
    if (payload && typeof payload === 'object') {
        const record = payload as Record<string, unknown>;
        const value = record.message ?? record.detail;
        return typeof value === 'string' ? value : undefined;
    }
    return undefined;
};

export default function PaymentNRAExport({ parkingId, dateTime, disabled }: Props) {
    const { mutate, isPending } = useExportPaymentsNRA();
    const { toast } = useToast();
    const { formatMessage } = useIntl();

    const onExport = () => {
        let beforeDate = dayjs(dateTime.beforeDate);
        beforeDate = beforeDate.subtract(1, 'days');
        mutate(
            {
                parkingId,
                date_after: toDateParam(dateTime.afterDate),
                date_before: toDateParam(beforeDate.toDate()),
            },
            {
                onSuccess: (blob) => {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    const { formattedDate } = getFormattedDateTime(new Date().toISOString());
                    link.href = url;
                    link.download = `NRA_${formattedDate}.xml`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                },
                onError: async (error) => {
                    const description =
                        (await extractErrorMessage(error)) ?? formatMessage(generalErrorMessages.unknownError);
                    toast({
                        title: formatMessage(generalErrorMessages.somethingWentWrong),
                        description,
                        variant: 'destructive',
                    });
                },
            },
        );
    };

    return (
        <>
            <LoadingModal
                isLoading={isPending}
                title={<FormattedMessage {...statisticsMessages.exportingPayments} />}
            />
            <Button onClick={onExport} disabled={disabled || isPending}>
                <FormattedMessage {...generalMessages.exportNRA} />
            </Button>
        </>
    );
}
